import React, {PureComponent} from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import Button from '@adsk/bim360-matrix-react-button';
import IconButton from '@adsk/bim360-matrix-react-icon-button';
import Loader from '@adsk/bim360-matrix-react-loader';
import SvgIcon from '@adsk/bim360-matrix-react-svg-icon';
import {
  activePushpinAttributesType,
  documentType,
  initialFieldValuesType,
  pendingIssueAttributesType
} from '../../constants/proptype-validation';
import Link from '../lib/Link';
import i18n from '../../utils/i18n';
import {screenType} from '../../constants/analytics';
import IssueCreateFormContainer from '../issues/IssueCreateFormContainer';
import targetIcon from '../../assets/images/icons/icon-target.svg';
import closeIcon from '../../assets/images/icons/icon-close.svg';
import arrowBackGray from '../../assets/images/icons/icon-arrow-back-gray.svg';
import {TYPE_QUALITY_ISSUES} from '../../constants/issue-types';
import {
  beginPushpinWorkflow,
  endDropPushpinAction,
  endPushpinWorkflow,
  startDropPushpinAction,
  updatePushpinAction
} from '../../app/actions';
import {actions as formActions} from 'react-redux-form';
import {
  getActivePushpinAttributes,
  getAppLoading,
  getCurrentDocument,
  getFieldIssueMandatoryDataLoading,
  getPendingIssue,
  getTargetUrn
} from '../../app/selectors';
import {connect} from '../../app/connect';

class IssueNew extends PureComponent {
  constructor(...args) {
    super(...args);
    this.state = {
      isPushPinAlertOpen: true,
      isSubmitting: false,
      error: null
    };
    this._issueCreateFormContainer = React.createRef();
    this.handleSubmit = this._handleSubmit.bind(this);
    this.handleSaveStart = this._handleSaveStart.bind(this);
    this.handleSaveSuccess = this._handleSaveSuccess.bind(this);
    this.handleSaveError = this._handleSaveError.bind(this);
  }

  componentDidMount() {
    this.props.dispatch(startDropPushpinAction(this.props.pendingIssue.status));
    this.props.dispatch(beginPushpinWorkflow());
  }

  componentWillUpdate(nextProps) {
    if (nextProps.pendingIssue.status !== this.props.pendingIssue.status) {
      if (nextProps.issuePushpin) {
        // Update pushpin's color according to the new status
        this.props.dispatch(
          updatePushpinAction({
            id: nextProps.issuePushpin.id,
            label: nextProps.issuePushpin.label,
            status: nextProps.pendingIssue.status,
            position: nextProps.issuePushpin.position,
            objectId: nextProps.issuePushpin.objectId,
            viewerState: nextProps.issuePushpin.viewerState,
            externalId: nextProps.issuePushpin.externalId,
            attributesVersion: nextProps.issuePushpin.attributesVersion
          })
        );
      } else {
        // Restart pushpin drop (in viewer) for refreshing the pushpin color according to the new status
        this.props.dispatch(endDropPushpinAction());
        this.props.dispatch(startDropPushpinAction(nextProps.pendingIssue.status));
      }
    }
  }

  componentWillUnmount() {
    this.props.dispatch(endDropPushpinAction());
    this.props.dispatch(formActions.reset('pendingIssue'));
    this.props.dispatch(formActions.resetValidity('pendingIssue'));
    this.props.dispatch(endPushpinWorkflow());
  }

  _handleSubmit() {
    this._issueCreateFormContainer.current?.saveIssue();
  }

  _handleSaveStart() {
    this.setState({
      isSubmitting: true,
      error: null
    });
  }

  _handleSaveSuccess(successResponse) {
    const {onSuccess} = this.props;
    this.setState({isSubmitting: false});
    onSuccess && onSuccess(successResponse);
  }

  _handleSaveError(error) {
    this.setState({
      isSubmitting: false,
      error: error || true
    });
  }

  renderPushPinToast() {
    const {issuePushpin} = this.props;
    const left = `${window.innerWidth / 2}px`;

    if (issuePushpin || !this.state.isPushPinAlertOpen) {
      return null;
    }
    return (
      <div className="isui-form__push-pin-toast" style={{left}}>
        <SvgIcon className="isui-form__push-pin-toast-icon" width="24px" height="24px">
          {targetIcon}
        </SvgIcon>
        {i18n.t('new.pushPinAlert', {issueType: TYPE_QUALITY_ISSUES})}

        <IconButton
          className="isui-form__push-pin-toast-close"
          onClick={() => {
            this.setState({isPushPinAlertOpen: false});
          }}
        >
          <SvgIcon width="13px" height="13px">
            {closeIcon}
          </SvgIcon>
        </IconButton>
      </div>
    );
  }

  render() {
    const {
      issuePushpin,
      document,
      targetUrn,
      isSaving,
      showBackLink,
      isLoading,
      initialFieldValues,
      availableStatuses,
      headerTitle,
      titleFieldLabel,
      titleFieldRequired
    } = this.props;
    const {isSubmitting, error} = this.state;
    const isSaveInProgress = isSubmitting || isSaving;
    const disabled = !issuePushpin;
    const saveButtonClasses = cx('IssueNew__create-button', {
      'IssueNew__create-button--saving': isSaveInProgress
    });
    const saveButtonText = isSaveInProgress ? i18n.t('new.creating') : i18n.t('new.create');
    return (
      <div className="IssueNew">
        <div className="IssueNew__header">
          {showBackLink && (
            <Link className="IssueNew__back-link" to={`/${TYPE_QUALITY_ISSUES}`}>
              <SvgIcon width="11px" height="17px">
                {arrowBackGray}
              </SvgIcon>
            </Link>
          )}

          {headerTitle ?? i18n.t('new.addHeader', {issueType: TYPE_QUALITY_ISSUES})}
        </div>
        {this.renderPushPinToast()}
        {isLoading ? (
          <Loader />
        ) : (
          <IssueCreateFormContainer
            ref={this._issueCreateFormContainer}
            origin={screenType.PANEL}
            onSaveStart={this.handleSaveStart}
            onSaveSuccess={this.handleSaveSuccess}
            onSaveError={this.handleSaveError}
            handleSave={this.props.handleSave}
            disabled={disabled}
            isSubmitting={isSaveInProgress}
            error={error}
            focusForm={!disabled}
            issuePushpin={issuePushpin}
            document={document}
            targetUrn={targetUrn}
            container={this}
            initialFieldValues={initialFieldValues}
            availableStatuses={availableStatuses}
            titleFieldLabel={titleFieldLabel}
            titleFieldRequired={titleFieldRequired}
          />
        )}
        <div className="IssueNew__footer">
          <Button
            data-testid="IssueNew__cancelButton"
            styleType={Button.StyleType.LINK_SECONDARY}
            onClick={this.props.onCancel}
          >
            {i18n.t('navigation.cancel')}
          </Button>
          <Button
            styleType={Button.StyleType.PRIMARY}
            className={saveButtonClasses}
            data-testid="IssueNew__createButton"
            onClick={this.handleSubmit}
            disabled={disabled || isSaveInProgress || isLoading}
          >
            {saveButtonText}
          </Button>
        </div>
      </div>
    );
  }
}

IssueNew.displayName = 'IssueNew';

IssueNew.propTypes = {
  dispatch: PropTypes.func.isRequired,
  pendingIssue: pendingIssueAttributesType,
  issuePushpin: activePushpinAttributesType,
  document: documentType.isRequired,
  targetUrn: PropTypes.string.isRequired,
  handleSave: PropTypes.func,
  isLoading: PropTypes.bool,
  isSaving: PropTypes.bool,
  onSuccess: PropTypes.func,
  onCancel: PropTypes.func.isRequired,
  showBackLink: PropTypes.bool,
  initialFieldValues: initialFieldValuesType,
  availableStatuses: PropTypes.array,
  headerTitle: PropTypes.string,
  titleFieldLabel: PropTypes.string,
  titleFieldRequired: PropTypes.bool
};

const mapStateToProps = state => ({
  pendingIssue: getPendingIssue(state),
  issuePushpin: getActivePushpinAttributes(state),
  document: getCurrentDocument(state),
  targetUrn: getTargetUrn(state),
  isLoading: getAppLoading(state) || getFieldIssueMandatoryDataLoading(state)
});

export default connect(mapStateToProps)(IssueNew);
